// Types for q2q.json, format version 3. The normative spec is docs/format/README.md.

export const FORMAT_VERSION = 3;

/** [x0, y0, x1, y1] in PDF default user space (points, y up), x0 < x1, y0 < y1. */
export type Rect = [number, number, number, number];
export type Point = [number, number];

export type RegionAnchor = { page: number; shape: "region"; rects: Rect[] };
export type PinAnchor = { page: number; shape: "pin"; point: Point };
export type Anchor = RegionAnchor | PinAnchor;

export interface Source {
  path: string;
  sha256: string;
  pageCount: number;
}

export interface ProjectInfo {
  title: string;
  source: Source;
}

export interface Page {
  page: number;
  rotate: 0 | 90 | 180 | 270;
  /** CropBox ∩ MediaBox in default user space. */
  view: Rect;
  /** Printed page label; "" when the page has none. */
  label: string;
}

export interface Department {
  id: string;
  code: string;
  name: string;
  color: string;
  order: number;
}

export interface NoteLayer {
  id: string;
  name: string;
  color: string;
  order: number;
}

export interface Moment {
  id: string;
  anchor: Anchor;
  scriptText: string;
}

export interface Cue {
  id: string;
  moment: string;
  department: string;
  number: string;
  label: string;
  notes: string;
}

export interface Standby {
  id: string;
  anchor: Anchor;
  /** The last cue it warns (a cue id), or "" when it has none yet. */
  through: string;
}

export type NoteStyle = "box" | "strike";
export type NotePlacement = "margin" | "inside";

export interface Note {
  id: string;
  layer: string;
  anchor: Anchor;
  style: NoteStyle;
  /** Omitted means margin; inside replaces the selected boxed region in rendered views. */
  placement?: NotePlacement;
  text: string;
}

/** The whole q2q.json document. */
export interface Doc {
  format: "q2q";
  formatVersion: typeof FORMAT_VERSION;
  project: ProjectInfo;
  pages: Page[];
  departments: Department[];
  noteLayers: NoteLayer[];
  moments: Moment[];
  cues: Cue[];
  standbys: Standby[];
  notes: Note[];
}

/** The id-keyed record arrays. */
export interface IdRecords {
  departments: Department;
  noteLayers: NoteLayer;
  moments: Moment;
  cues: Cue;
  standbys: Standby;
  notes: Note;
}
export type IdRecordType = keyof IdRecords;
export const ID_RECORD_TYPES: readonly IdRecordType[] = ["departments", "noteLayers", "moments", "cues", "standbys", "notes"];

/** Every record type an edit can touch: the id-keyed arrays, page table entries (keyed by page) and the project. */
export interface Records extends IdRecords {
  pages: Page;
  project: ProjectInfo;
}
export type RecordType = keyof Records;
export type AnyRecord = Records[RecordType];

export const ID_PREFIX: Record<IdRecordType, string> = {
  departments: "d",
  noteLayers: "l",
  moments: "m",
  cues: "c",
  standbys: "s",
  notes: "n",
};
